<template>
	<div
		ref="ruler"
		class="ruler"
	>
		<div
			ref="scroll"
			class="ruler__click-w"
			:style="scrollStyle"
			@click.stop="handleClick"
			@mousedown.stop="handleMouseDown"
		>
			<RulerStep
				v-for="step of steps"
				:id="step.key"
				:key="step.key"
				:is-mobile-web-app="isMobileWebApp"
				:type="step.type"
				:value="step.value"
				:label="step.label"
				:style="{
					left: `${step.percent}%`,
				}"
			/>
		</div>
	</div>
</template>

<script>
import { RULER_FORMAT_MINUTES, RULER_SCALES } from './constants';
import RulerStep from './RulerStep.vue';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';

export default {
	name:       'Ruler',
	components: {
		RulerStep,
	},
	props: {
		duration: {
			type:    Number,
			default: 0,
		},
		zoomStart: {
			type:    Number,
			default: 0,
		},
		zoomLevel: {
			type:    Number,
			default: 1,
		},
		isMobileWebApp: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			visibleDuration:  0,
			isNeedleDragging: false,
		};
	},
	computed: {
		scrollStyle() {
			const { duration, zoomLevel, zoomStart } = this;
			if (this.isMobileWebApp) {
				return {
					width:     '100%',
					transform: 'none',
				};
			}
			const width = Math.round(Math.max(100, zoomLevel * 100));
			const left = (-1 * (zoomStart * 100)) / duration;
			return {
				width:     `${width}%`,
				transform: `translateX(${left}%)`,
			};
		},
		steps() {
			const duration = this.duration;
			const visibleDuration = this.visibleDuration;

			if (!this.visibleDuration) {
				return [];
			}

			let scale = null;
			for (const s of RULER_SCALES) {
				if (s.TRIGGER && visibleDuration > s.TRIGGER) {
					if (!scale || !scale.TRIGGER || (scale && scale.TRIGGER && scale.TRIGGER < s.TRIGGER)) {
						scale = s;
					}
				} else if (!s.TRIGGER && !scale) {
					scale = s;
				}
			}

			const steps = ['MAJOR', 'MEDIUM', 'MINOR']
				.map((key) => {
					if (!scale[key]) return [];
					const interval = scale[key];
					const scaleSteps = [];
					for (let i = 0; i <= Math.ceil(duration / interval); i++) {
						const value = i * interval;
						const percent = (value * 100) / duration;
						const label = this.formatTimestamp(value, scale.format);
						scaleSteps.push({
							key:  `${key}-${i}`,
							type: key,
							label,
							value,
							percent,
						});
					}
					return scaleSteps;
				})
				.reduce((acc, curr) => {
					for (const step of curr) {
						if (!acc[step.value]) acc[step.value] = step;
					}
					return acc;
				}, {});

			let visibleSteps = Object.values(steps).reduce((acc, curr) => {
				acc.push(curr);
				return acc;
			}, []);

			// optimization: keep only the steps that are roughly visible (recycler sort of)
			visibleSteps = visibleSteps.filter(step => step.value > this.zoomStart - 100 && step.value < this.zoomStart + this.visibleDuration + 100);

			return visibleSteps;
		},
	},
	updated() {
		this.updateVisibleDuration();
	},
	mounted() {
		this.updateVisibleDuration();
	},
	methods: {
		updateVisibleDuration() {
			const { ruler, scroll } = this.$refs;
			if (!ruler || !scroll) {
				throw new Error('Cannot find ruler or scroll ref.');
			}
			// TODO: refactor to use zoomlevel
			const rulerWidth = ruler.getBoundingClientRect().width;
			const scrollWidth = scroll.getBoundingClientRect().width;
			const ratio = rulerWidth / scrollWidth;
			this.visibleDuration = this.duration * ratio;
		},
		handleClick(e) {
			this.moveNeedle(e.clientX);
		},
		handleMouseDown() {
			document.addEventListener('mousemove', this.handleMouseMove);
			document.addEventListener('mouseup', this.handleMouseUp);
			this.isNeedleDragging = true;
		},
		handleMouseMove(e) {
			if (this.isNeedleDragging) {
				this.moveNeedle(e.clientX);
			}
		},
		handleMouseUp() {
			this.isNeedleDragging = false;
			document.removeEventListener('mousemove', this.handleMouseMove);
			document.removeEventListener('mousemove', this.handleMouseUp);
		},
		moveNeedle(clientX) {
			const ref = this.$refs.scroll;
			if (!ref) return;
			let x = clientX;
			const rect = ref.getBoundingClientRect();
			const width = ref.offsetWidth;

			// offset X
			x -= rect.x;
			if (x < 0) x = 0;

			const val = (x * 100) / width;

			this.$emit('seek', val);
		},
		formatTimestamp(timestamp, format) {
			return format === RULER_FORMAT_MINUTES ? `${timestamp / 60}` : formatTimestampAsMinutesSeconds(timestamp);
		},
	},
};
</script>

<style lang="scss" scoped>
@import './timeline.scss';

.ruler {
	position: relative;
	height: 100%;
}

.ruler__click-w {
	position: relative;
	height: 100%;
	padding: 0;
}
</style>
